import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { ComponentPropsWithoutRef, useState } from 'react'

import { Carousel } from '@/features/carousel'
import { mq, widthInCols } from '@/theme/mixins'

import { MediaCarouselImage } from './MediaCarouselImage'
import { MediaCarouselVideo } from './MediaCarouselVideo'

export type MediaCarouselLayout = 'ARTICLE' | 'HIGHLIGHT'

interface Props extends ComponentPropsWithoutRef<'div'> {
  data?: Queries.MediaCarouselFragment | null
  layout: MediaCarouselLayout
}

export const MediaCarousel = ({ data, layout, ...props }: Props) => {
  const [height, setHeight] = useState<number | null>(null)
  const styles = {
    slider: css`
      ${layout === 'ARTICLE' &&
      css`
        grid-column: 1 / -1;
        --border-thickness: 1rem;
        --gtr: calc(2 * var(--border-thickness) + var(--gtr-36));
        --slide-width: ${widthInCols({ count: 10 })};
        --scroll-width: calc(var(--slide-width) + var(--gtr));
        margin: 1em 0 2.5em;
        height: calc(${height}px + 2rem);
        transition: height 750ms ease;
        ${mq().ml} {
          --slide-width: ${widthInCols({ count: 11 })};
        }
        ${mq().s} {
          --border-thickness: 0.75rem;
          --slide-width: calc(var(--grid-w) - 3 * var(--margin));
        }
        &[data-class-note] {
          --slide-width: ${widthInCols({ count: 12 })};
          ${mq().ml} {
            --slide-width: ${widthInCols({ count: 12 })};
          }
        }
      `}
      ${layout === 'HIGHLIGHT' &&
      css`
        --gtr: 0.5rem;
        --border-thickness: 0px;
        --slide-width: calc(
          (100vw - 2 * var(--margin) - var(--gtr-36)) * 7 / 12
        );
        --scroll-width: calc(var(--slide-width) + var(--gtr));
        height: 100%;
        ${mq().ms} {
          --slide-width: calc(100vw - 2 * var(--margin));
        }
        ${mq().s} {
          --slide-width: calc(100vw - 2 * var(--gtr-18));
        }
      `}
    `,
    sliderContent: css`
      display: grid;
      grid-column-gap: var(--gtr);
      grid-template-columns: repeat(
        ${data?.media?.length},
        var(--slide-width, auto)
      );
      ${layout === 'ARTICLE' &&
      css`
        padding: 0
          calc(
            var(--margin) + ${widthInCols({ count: 2 })} + var(--gtr)
          )
          0 var(--margin);
        ${mq().ml} {
          padding: 0
            calc(
              var(--margin) + ${widthInCols({ count: 1 })} + var(--gtr)
            )
            0 var(--margin);
        }
        ${mq().s} {
          padding: 0 calc(1.5 * var(--margin));
        }
        [data-class-note] & {
          padding: 0 var(--margin);
          ${mq().ml} {
            padding: 0 var(--margin);
          }
        }
      `}
      ${layout === 'HIGHLIGHT' &&
      css`
        padding: 0;
        > * {
          height: 100%;
        }
      `}
    `,
    scrollArea: css`
      ${layout === 'ARTICLE' &&
      css`
        ${mq().s} {
          scroll-padding: 0 calc(1.5 * var(--margin));
        }
      `}
      ${layout === 'HIGHLIGHT' &&
      css`
        scroll-padding: 0;
        height: 100%;
      `}
    `,
    nav: css`
      ${layout === 'ARTICLE' &&
      css`
        width: calc(var(--slide-width) + 2 * var(--margin));
        ${mq().s} {
          width: 100%;
        }
      `}
    `,
    videoContainer: css`
      background: rgba(0, 0, 0, 0.9);
      display: grid;
      position: relative;
      align-self: flex-start;
    `,
  }
  return (
    <Carousel
      css={styles.slider}
      contentCss={styles.sliderContent}
      navCss={styles.nav}
      scrollAreaCss={styles.scrollArea}
      snap
      navVariant={layout === 'ARTICLE' ? 'OVERLAY' : 'CORNER'}
      {...props}
    >
      {data?.media?.map((block, i) => {
        switch (block?.__typename) {
          case 'DatoCmsImageBlock':
            return (
              <MediaCarouselImage
                data={block}
                carouselHeight={height}
                setCarouselHeight={
                  layout !== 'HIGHLIGHT' ? setHeight : undefined
                }
                layout={layout}
                key={i}
              />
            )
          case 'DatoCmsExternalVideoBlock':
            return (
              <div css={styles.videoContainer}>
                <MediaCarouselVideo
                  data={block}
                  key={i}
                  layout={layout}
                  carouselHeight={height}
                  setCarouselHeight={
                    layout !== 'HIGHLIGHT' ? setHeight : undefined
                  }
                />
              </div>
            )
        }
      })}
    </Carousel>
  )
}

export const MediaCarouselFragment = graphql`
  fragment MediaCarousel on DatoCmsMediaCarousel {
    __typename
    id: originalId
    media {
      ... on DatoCmsImageBlock {
        ...MediaCarouselImage
      }
      ... on DatoCmsExternalVideoBlock {
        ...ExternalVideoBlock
      }
    }
  }
`
